import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebaseDDH';
import { useAuth } from '../../context/AuthContext';
import { createTrackingLink, trackingLinkUrl } from '../../utils/trackingLinks';
import TrackingLinksModal from './TrackingLinksModal';
import IncludedItemsModal from './IncludedItemsModal';
import DescriptionItemsInput from './DescriptionItemsInput';
import { MONO } from '../../constants/fonts';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  input: {
    flex: 1,
    minWidth: 160,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  button: {
    padding: '10px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#111',
    color: '#fff',
    fontSize: '0.95rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  linkBox: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    padding: 10,
    background: '#f6f6f4',
    borderRadius: 8,
  },
  linkText: {
    flex: 1,
    fontFamily: MONO,
    fontSize: '0.8rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  copyButton: {
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  error: {
    color: '#c0392b',
    fontSize: '0.85rem',
    marginBottom: 12,
  },
  offerSelect: {
    width: '100%',
    padding: '10px 36px 10px 12px',
    marginBottom: 8,
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    background: '#fff',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
    backgroundSize: '11px 7px',
  },
  offerFields: {
    marginBottom: 12,
  },
  sectionLabel: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#555',
    margin: '4px 0 8px',
  },
  categoryRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  categoryButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    background: '#fff',
    color: '#555',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  categoryButtonActive: {
    background: '#111',
    color: '#fff',
    border: '1px solid #111',
  },
  priceInputWrap: {
    position: 'relative',
  },
  priceSymbol: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#888',
    fontSize: '0.95rem',
    pointerEvents: 'none',
  },
  priceInput: {
    width: '100%',
    padding: '10px 12px 10px 26px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    marginTop: 8,
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: 60,
    fontFamily: 'inherit',
  },
  meta: {
    color: '#888',
    fontSize: '0.78rem',
    marginBottom: 12,
  },
  viewButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#222',
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  presetsButton: {
    width: '100%',
    marginTop: 8,
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px dashed #c5c5cc',
    background: '#fafafa',
    color: '#555',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      style={styles.copyButton}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export default function TrackingLinksPanel() {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('website');
  const [offerType, setOfferType] = useState('none');
  const [discountPercent, setDiscountPercent] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [essentialMode, setEssentialMode] = useState('none');
  const [essentialPercent, setEssentialPercent] = useState('');
  const [essentialCustom, setEssentialCustom] = useState('');
  const [activeMode, setActiveMode] = useState('none');
  const [activePercent, setActivePercent] = useState('');
  const [activeCustom, setActiveCustom] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [reused, setReused] = useState(false);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [includedItemsModalOpen, setIncludedItemsModalOpen] = useState(false);

  const applyPresets = (items) => {
    setCustomDescription((prev) => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}\n${items.join('\n')}` : items.join('\n');
    });
  };

  useEffect(() => {
    const q = query(collection(db, 'trackingLinks'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setLinks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const offer = offerType === 'none' ? null : {
        type: offerType,
        discountPercent: offerType === 'discount' ? Number(discountPercent) : undefined,
        customPrice: offerType === 'custom' ? `£${customPrice.trim()}` : undefined,
        customDescription: (offerType === 'custom' || offerType === 'free') ? customDescription.trim() : undefined,
      };
      const carePlan = (essentialMode === 'none' && activeMode === 'none') ? null : {
        essential: essentialMode === 'none' ? undefined : {
          mode: essentialMode,
          percent: essentialMode === 'percent' ? Number(essentialPercent) : undefined,
          custom: essentialMode === 'custom' ? Number(essentialCustom) : undefined,
        },
        active: activeMode === 'none' ? undefined : {
          mode: activeMode,
          percent: activeMode === 'percent' ? Number(activePercent) : undefined,
          custom: activeMode === 'custom' ? Number(activeCustom) : undefined,
        },
      };
      const { token, reused: wasReused } = await createTrackingLink(name, user.email, offer, category, carePlan);
      setNewUrl(trackingLinkUrl(token));
      setReused(wasReused);
      setName('');
      setCategory('website');
      setOfferType('none');
      setDiscountPercent('');
      setCustomPrice('');
      setCustomDescription('');
      setEssentialMode('none');
      setEssentialPercent('');
      setEssentialCustom('');
      setActiveMode('none');
      setActivePercent('');
      setActiveCustom('');
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>Tracking links</div>
      <p style={styles.description}>Use these links to track visits to our website, try not to click them yourself.</p>
      <form onSubmit={handleSubmit}>
        <div style={styles.row}>
          <input
            style={styles.input}
            type="text"
            placeholder="Name (e.g. Microsoft)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <button type="submit" style={styles.button} disabled={submitting}>
            Create link
          </button>
        </div>

        <div style={styles.categoryRow}>
          <button
            type="button"
            style={{ ...styles.categoryButton, ...(category === 'website' ? styles.categoryButtonActive : {}) }}
            onClick={() => setCategory('website')}
          >
            Website
          </button>
          <button
            type="button"
            style={{ ...styles.categoryButton, ...(category === 'app' ? styles.categoryButtonActive : {}) }}
            onClick={() => setCategory('app')}
          >
            App
          </button>
        </div>
        <div style={styles.sectionLabel}>Base build price</div>
        <select
          style={styles.offerSelect}
          value={offerType}
          onChange={(e) => setOfferType(e.target.value)}
        >
          <option value="none">No special offer</option>
          <option value="discount">Discount %</option>
          <option value="free">Free</option>
          <option value="custom">Custom price</option>
        </select>

        {offerType === 'discount' && (
          <div style={styles.offerFields}>
            <input
              style={{ ...styles.input, width: '100%' }}
              type="number"
              min="1"
              max="100"
              placeholder="Discount percentage (e.g. 20)"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              required
            />
          </div>
        )}

        {(offerType === 'custom' || offerType === 'free') && (
          <div style={styles.offerFields}>
            {offerType === 'custom' && (
              <div style={styles.priceInputWrap}>
                <span style={styles.priceSymbol}>£</span>
                <input
                  style={styles.priceInput}
                  type="text"
                  placeholder="4,000"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  required
                />
              </div>
            )}
            <div style={styles.sectionLabel}>Included in build</div>
            <DescriptionItemsInput value={customDescription} onChange={setCustomDescription} />
            <button type="button" style={styles.presetsButton} onClick={() => setIncludedItemsModalOpen(true)}>
              + Add included items
            </button>
          </div>
        )}

        <div style={styles.sectionLabel}>Essential plan discount</div>
        <select
          style={styles.offerSelect}
          value={essentialMode}
          onChange={(e) => setEssentialMode(e.target.value)}
        >
          <option value="none">No change</option>
          <option value="percent">% off</option>
          <option value="free">Free</option>
          <option value="custom">Custom price</option>
        </select>

        {essentialMode === 'percent' && (
          <div style={styles.offerFields}>
            <input
              style={{ ...styles.input, width: '100%' }}
              type="number"
              min="1"
              max="100"
              placeholder="Percentage off (e.g. 20)"
              value={essentialPercent}
              onChange={(e) => setEssentialPercent(e.target.value)}
              required
            />
          </div>
        )}

        {essentialMode === 'custom' && (
          <div style={styles.offerFields}>
            <div style={styles.priceInputWrap}>
              <span style={styles.priceSymbol}>£</span>
              <input
                style={styles.priceInput}
                type="text"
                placeholder="e.g. 20"
                value={essentialCustom}
                onChange={(e) => setEssentialCustom(e.target.value)}
                required
              />
            </div>
          </div>
        )}

        <div style={styles.sectionLabel}>Active plan discount</div>
        <select
          style={styles.offerSelect}
          value={activeMode}
          onChange={(e) => setActiveMode(e.target.value)}
        >
          <option value="none">No change</option>
          <option value="percent">% off</option>
          <option value="free">Free</option>
          <option value="custom">Custom price</option>
        </select>

        {activeMode === 'percent' && (
          <div style={styles.offerFields}>
            <input
              style={{ ...styles.input, width: '100%' }}
              type="number"
              min="1"
              max="100"
              placeholder="Percentage off (e.g. 20)"
              value={activePercent}
              onChange={(e) => setActivePercent(e.target.value)}
              required
            />
          </div>
        )}

        {activeMode === 'custom' && (
          <div style={styles.offerFields}>
            <div style={styles.priceInputWrap}>
              <span style={styles.priceSymbol}>£</span>
              <input
                style={styles.priceInput}
                type="text"
                placeholder="e.g. 40"
                value={activeCustom}
                onChange={(e) => setActiveCustom(e.target.value)}
                required
              />
            </div>
          </div>
        )}
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {newUrl && (
        <div style={styles.linkBox}>
          <div style={styles.linkText}>{newUrl}</div>
          <CopyButton text={newUrl} />
        </div>
      )}
      {newUrl && reused && (
        <div style={styles.meta}>This company already had a link — reusing it, stats untouched.</div>
      )}

      <button type="button" style={styles.viewButton} onClick={() => setModalOpen(true)}>
        {loading ? 'View links' : `View links (${links.length})`}
      </button>

      {modalOpen && (
        <TrackingLinksModal links={links} loading={loading} onClose={() => setModalOpen(false)} />
      )}

      {includedItemsModalOpen && (
        <IncludedItemsModal
          existingDescription={customDescription}
          onApply={applyPresets}
          onClose={() => setIncludedItemsModalOpen(false)}
        />
      )}
    </div>
  );
}
